// Shared header strips for the three derivative widgets — keeps the picker
// declarations co-located so adding a new control (e.g. a smoothing toggle)
// only touches one file. Each header is exported individually; the page maps
// it onto the corresponding widget via `headerExtras`.

import React from 'react';
import { FilterDropdown } from '../../CoinTerminalSections/sectionHelpers';
import { useWidgetSetting } from '../cockpitContext.jsx';
import CoinPickerHeader from './CoinPickerHeader';

const INTERVAL_OPTIONS = [
  { label: '1m', value: '1m' },
  { label: '5m', value: '5m' },
  { label: '15m', value: '15m' },
  { label: '30m', value: '30m' },
  { label: '1h', value: '1h' },
  { label: '4h', value: '4h' },
  { label: '1d', value: '1d' },
];

const HOURS_OPTIONS = [
  { label: '24h', value: 24 },
  { label: '48h', value: 48 },
  { label: '7d',  value: 168 },
];

const LIQ_MIN_SIZE_OPTIONS = [
  { label: 'All',  value: 0 },
  { label: '10K+', value: 10_000 },
  { label: '100K+', value: 100_000 },
  { label: '500K+', value: 500_000 },
  { label: '1M+',  value: 1_000_000 },
  { label: '5M+',  value: 5_000_000 },
];

const LIQ_VOL_MODES = [
  { label: 'Mirror', value: 'mirror' },
  { label: 'Long',   value: 'long' },
  { label: 'Short',  value: 'short' },
  { label: 'Net',    value: 'net' },
];

const LS_SOURCE_OPTIONS = [
  { label: 'Global',     value: 'global' },
  { label: 'Top·Acct',   value: 'topAccounts' },
  { label: 'Top·Pos',    value: 'topPositions' },
  { label: 'Taker B/S',  value: 'taker' },
];

const LS_PERIOD_OPTIONS = [
  { label: '5m',  value: '5m' },
  { label: '15m', value: '15m' },
  { label: '30m', value: '30m' },
  { label: '1h',  value: '1h' },
  { label: '4h',  value: '4h' },
  { label: '12h', value: '12h' },
  { label: '1d',  value: '1d' },
];

const LS_LIMIT_OPTIONS = [
  { label: '100',  value: 100 },
  { label: '250',  value: 250 },
  { label: '500',  value: 500 },
];

const LS_AREA_OPTIONS = [
  { label: 'Area', value: true },
  { label: 'Line', value: false },
];

const TV_MODE_OPTIONS = [
  { label: 'Split',   value: 'split' },
  { label: 'Stacked', value: 'stacked' },
  { label: 'Total',   value: 'total' },
];

const TV_CVD_OPTIONS = [
  { label: 'CVD off', value: false },
  { label: 'CVD on',  value: true },
];

// === Liq Volume header ===
function LiqVolumeHeaderImpl({ instanceId, defaultCoin }) {
  const [interval, setInterval] = useWidgetSetting(instanceId, 'interval', '5m');
  const [hours, setHours] = useWidgetSetting(instanceId, 'hours', 24);
  const [minSize, setMinSize] = useWidgetSetting(instanceId, 'minSize', 0);
  const [mode, setMode] = useWidgetSetting(instanceId, 'liqVolumeMode', 'mirror');
  return (
    <>
      <FilterDropdown ariaLabel="Interval" options={INTERVAL_OPTIONS} value={interval} onChange={setInterval} />
      <FilterDropdown ariaLabel="Lookback" options={HOURS_OPTIONS} value={hours} onChange={setHours} />
      <FilterDropdown icon="liq" ariaLabel="Min liquidation size" options={LIQ_MIN_SIZE_OPTIONS} value={minSize} onChange={setMinSize} />
      <FilterDropdown ariaLabel="Display mode" options={LIQ_VOL_MODES} value={mode} onChange={setMode} />
      <CoinPickerHeader instanceId={instanceId} defaultCoin={defaultCoin} />
    </>
  );
}
export const LiqVolumeHeader = React.memo(LiqVolumeHeaderImpl);

// === Long/Short header ===
function LongShortHeaderImpl({ instanceId, defaultCoin }) {
  const [source, setSource] = useWidgetSetting(instanceId, 'lsSource', 'global');
  const [period, setPeriod] = useWidgetSetting(instanceId, 'lsPeriod', '5m');
  const [limit, setLimit] = useWidgetSetting(instanceId, 'lsLimit', 500);
  const [areaFill, setAreaFill] = useWidgetSetting(instanceId, 'lsAreaFill', true);
  return (
    <>
      <FilterDropdown ariaLabel="Long/short source" options={LS_SOURCE_OPTIONS} value={source} onChange={setSource} />
      <FilterDropdown ariaLabel="Period" options={LS_PERIOD_OPTIONS} value={period} onChange={setPeriod} />
      <FilterDropdown ariaLabel="Lookback points" options={LS_LIMIT_OPTIONS} value={limit} onChange={setLimit} />
      <FilterDropdown ariaLabel="Render style" options={LS_AREA_OPTIONS} value={areaFill} onChange={setAreaFill} />
      <CoinPickerHeader instanceId={instanceId} defaultCoin={defaultCoin} />
    </>
  );
}
export const LongShortHeader = React.memo(LongShortHeaderImpl);

// === Trade Volume header ===
function TradeVolumeHeaderImpl({ instanceId, defaultCoin }) {
  const [interval, setInterval] = useWidgetSetting(instanceId, 'tvInterval', '5m');
  const [hours, setHours] = useWidgetSetting(instanceId, 'tvHours', 24);
  const [mode, setMode] = useWidgetSetting(instanceId, 'tvMode', 'split');
  const [showCvd, setShowCvd] = useWidgetSetting(instanceId, 'tvShowCvd', false);
  return (
    <>
      <FilterDropdown ariaLabel="Interval" options={INTERVAL_OPTIONS} value={interval} onChange={setInterval} />
      <FilterDropdown ariaLabel="Lookback" options={HOURS_OPTIONS} value={hours} onChange={setHours} />
      <FilterDropdown ariaLabel="Display mode" options={TV_MODE_OPTIONS} value={mode} onChange={setMode} />
      <FilterDropdown ariaLabel="Cumulative delta overlay" options={TV_CVD_OPTIONS} value={showCvd} onChange={setShowCvd} />
      <CoinPickerHeader instanceId={instanceId} defaultCoin={defaultCoin} />
    </>
  );
}
export const TradeVolumeHeader = React.memo(TradeVolumeHeaderImpl);
